import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {UserService} from '../services/user/user.service';
import {NavigationService} from '../services/nav/navigation.service';
import {JwtService} from '../services/user/jwt.service';
import {UserDto} from '@api/utisha-backend';
import RolesEnum = UserDto.RolesEnum;
import UserTypeEnum = UserDto.UserTypeEnum;

export type AppRole = 'ADMIN' | 'TEACHER' | 'STUDENT' | 'PLATFORM_ADMIN';

@Injectable({providedIn: 'root'})
export class RoleGuard {

  constructor(
    private userService: UserService,
    private navService: NavigationService,
    private router: Router,
    private jwt: JwtService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    const required: AppRole[] = route.data?.['roles'] ?? [];

    // TEACHER and STUDENT are global user-type attributes stored in the Cognito
    // claim — they don't depend on school membership. Use the JWT directly so
    // non-members can browse any school's teacher/student sections without
    // triggering a school-scoped /users/me call (which would fail and kick off
    // the UnauthorizedInterceptor reload loop).
    const needsSchoolProfile = required.some(r => r === 'ADMIN' || r === 'PLATFORM_ADMIN');

    if (!needsSchoolProfile) {
      // STUDENT zone: any authenticated user is welcome — a teacher or
      // platform-admin visiting a school where they have no explicit role
      // gets the student experience rather than being blocked.
      if (required.every(r => r === 'STUDENT')) {
        const isAuth = await this.jwt.isAuthenticated();
        if (!isAuth) { this.navService.login(); return false; }
        return true;
      }

      const userType = await this.jwt.getUserType();
      if (userType) {
        const jwtAllowed = required.some(role =>
          (role === 'TEACHER' && userType === 'TEACHER') ||
          (role === 'STUDENT' && userType === 'STUDENT')
        );
        // Fast-path approval: JWT claim confirms the required role.
        // On mismatch we fall through to the backend check rather than
        // denying immediately — a school admin may have a stale or wrong
        // userType claim while still holding ADMIN/PLATFORM_ADMIN roles.
        if (jwtAllowed) return true;
      }

      // JWT claim absent or does not match — fall back to the backend profile.
      // Handles federated logins, platform admins, and accounts whose
      // custom:userType Cognito attribute does not reflect their actual role.
      const user = await this.userService.getUser().catch(() => null);
      if (!user) {
        this.navService.login();
        return false;
      }
      const backendRoles = user.roles ?? [];
      const isAdminOrPlatformAdmin = backendRoles.includes(RolesEnum.Admin) || backendRoles.includes(RolesEnum.PlatformAdmin);
      const allowed = required.some(role =>
        (role === 'TEACHER' && (user.userType === UserTypeEnum.Teacher || isAdminOrPlatformAdmin)) ||
        (role === 'STUDENT' && user.userType === UserTypeEnum.Student)
      );
      if (!allowed) this.router.navigate(['/']);
      return allowed;
    }

    // ADMIN / PLATFORM_ADMIN require the school-scoped user profile because
    // those roles are per-school, not in the JWT claim.
    const user = await this.userService.getUser().catch(() => null);
    if (!user) {
      this.navService.login();
      return false;
    }

    const roles = user.roles ?? [];
    const isPlatformAdmin = roles.includes(RolesEnum.PlatformAdmin);
    const allowed = required.some(role => {
      switch (role) {
        case 'ADMIN':          return roles.includes(RolesEnum.Admin) || isPlatformAdmin;
        case 'PLATFORM_ADMIN': return isPlatformAdmin;
        default:               return false;
      }
    });

    if (!allowed) this.router.navigate(['/']);
    return allowed;
  }
}
